import {Product, ProductVariant} from '../../../entities/product';

const cleanObject = obj => {
  const cleanedObj = {};

  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== null && !(Array.isArray(value) && value.length === 0)) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        cleanedObj[key] = cleanObject(value);
      } else {
        cleanedObj[key] = value;
      }
    }
  });

  return cleanedObj;
};

export const extractInfoProductField = (
  productData: any,
  variantData: any,
  type: string,
  productId: string
) => {
  let formatProduct;
  if (type === 'PRODUCT') {
    formatProduct = {
      products: [
        {
          productId: productId,
          stock: productData.stock,
        },
      ],
    };
  }

  if (type === 'VARIANT') {
    const {variants} = variantData;
    formatProduct = {
      products: [
        {
          productId: productId,
          variants: variants.map(variant => {
            const variantInfo = {
              variantId: variant.id,
              enabled: variant.enabled,
              offerPrice: variant.offerPrice,
              price: variant.price,
              sku: variant.sku,
              stock: variant.stock,
            };

            cleanObject(variantInfo);

            return variantInfo;
          }),
        },
      ],
    };
  }

  return {formatProduct};
};

export const extractInfoProduct = (
  productSelection: Product[],
  productVariantSelection: ProductVariant[]
) => {
  const productsInfoMap = new Map();
  let isValidNewOfferPrice = true;
  let isValidStock = true;

  productSelection.forEach(product => {
    if (product?.offerPrice === undefined || product?.offerPrice === null) {
      isValidNewOfferPrice = false;
    }

    if (product?.stockSetting?.enabled === false || !product?.stockSetting) {
      isValidStock = false;
    }

    if (product) {
      const initialProductInfo = {
        productId: product.id,
        price: product.price,
        offerPrice: product?.offerPrice || null,
        stock: product?.stockSetting?.stock || null,
        categories: product.categories?.map(c => c.id),
        variants: [],
      };

      productsInfoMap.set(product.id, cleanObject(initialProductInfo));
    }
  });

  productVariantSelection.forEach(productVariantS => {
    let product;
    const isVariantInProduct = productSelection.some(product =>
      product.variants.find(variant => variant.id === productVariantS.id)
    );
    if (productSelection.length > 0 && isVariantInProduct) {
      product = productSelection.find(p =>
        p.variants.find(variant => variant.id === productVariantS.id)
      );
    } else {
      const productId = productVariantS.productId;
      product = {
        id: productId,
        variants: [],
      };
    }

    const existingVariantIndex = product?.variants.findIndex(
      variant => variant.id === productVariantS.id
    );

    if (existingVariantIndex === -1) {
      const {productId, ...cleanedVariant} = productVariantS;
      product.id = productId;
      product.variants.push(cleanedVariant);
    }

    if (product && product?.variants) {
      product.variants = product?.variants.filter(variant => variant.id);
    }

    const productVariant = product?.variants.find(
      variant => variant.id === productVariantS.id
    );

    if (product && productVariant) {
      if (productsInfoMap.has(product.id)) {
        const productInfo = productsInfoMap.get(product.id);

        if (!productInfo.variants) {
          productInfo.variants = [];
        }

        productInfo.variants.push(
          cleanObject({
            variantId: productVariantS.id || productVariant.id,
            price: String(productVariantS?.price || productVariant?.price),
            offerPrice:
              productVariantS?.offerPrice || productVariant?.offerPrice,
            stock: productVariantS?.stock || productVariant?.stock,
            enabled: productVariantS?.enabled || productVariant?.enabled,
            enabledBulkPrice:
              productVariantS?.enabledBulkPrice ||
              productVariant?.enabledBulkPrice,
            sku: productVariantS?.sku || productVariant?.sku,
          })
        );
        productsInfoMap.set(product.id, productInfo);
      } else {
        productsInfoMap.set(product.id, {
          productId: product.id,
          price: product?.price,
          offerPrice: product?.offerPrice,
          stock: product?.stockSetting?.stock,
          categories: product?.categories?.map(c => c.id),
          variants: [
            cleanObject({
              variantId: productVariantS.id || productVariant.id,
              price: String(productVariantS?.price || productVariant?.price),
              offerPrice:
                productVariantS?.offerPrice || productVariant?.offerPrice,
              stock: productVariantS?.stock || productVariant?.stock,
              enabled: productVariantS?.enabled || productVariant?.enabled,
              enabledBulkPrice:
                productVariantS?.enabledBulkPrice ||
                productVariant?.enabledBulkPrice,
              sku: productVariantS?.sku || productVariant?.sku,
            }),
          ],
        });
      }
    }
  });

  const productsInfo = Array.from(productsInfoMap.values()).map(cleanObject);
  return {productsInfo, isValidNewOfferPrice, isValidStock};
};

export const updateDataProductsNewOfert = (
  productsInfo,
  productSelection,
  valueNewOfertNumber,
  optionSelectedType: 'FIXED' | 'PERCENTAGE'
) => {
  const updatedProductsInfo = productsInfo.map(productInfo => {
    const isInProductSelection = productSelection.some(
      productS => productS.id === productInfo.productId
    );

    if (
      isInProductSelection ||
      productInfo.offerPrice !== undefined ||
      valueNewOfertNumber !== null
    ) {
      let updatedOfferPrice;
      if (optionSelectedType === 'PERCENTAGE') {
        updatedOfferPrice =
          Number(productInfo.price) -
          (Number(productInfo.price) * valueNewOfertNumber) / 100;
      } else if (optionSelectedType === 'FIXED') {
        updatedOfferPrice = Number(valueNewOfertNumber);
      }

      const {price, taxedPrice, ...rest} = productInfo;

      const updatedProductInfo = {
        ...rest,
        offerPrice: isInProductSelection
          ? updatedOfferPrice.toFixed(2)
          : productInfo.offerPrice,
      };

      if (updatedProductInfo.variants) {
        updatedProductInfo.variants = updatedProductInfo.variants.map(
          ({taxedPrice, price, ...variant}) => {
            let updatedVariantOfferPrice;
            if (optionSelectedType === 'PERCENTAGE') {
              updatedVariantOfferPrice =
                Number(price) -
                (Number(price) * Number(valueNewOfertNumber)) / 100;
            } else if (optionSelectedType === 'FIXED') {
              updatedVariantOfferPrice = Number(valueNewOfertNumber);
            }

            return {
              ...variant,
              price,
              offerPrice: valueNewOfertNumber
                ? updatedVariantOfferPrice.toFixed(2)
                : variant.offerPrice,
            };
          }
        );
      }

      return cleanObject(updatedProductInfo);
    }

    return productInfo;
  });

  return updatedProductsInfo;
};

export const updateDataProductsNewPrice = (
  productsInfo,
  productSelection,
  valueNewPrice
) => {
  const updatedProductsInfo = productsInfo.map(productInfo => {
    const isInProductSelection = productSelection.some(
      productS => productS.id === productInfo.productId
    );

    if (
      isInProductSelection ||
      productInfo.price !== undefined ||
      valueNewPrice !== null
    ) {
      const {taxedPrice, offerPrice, categories, stock, ...rest} = productInfo;
      const updatedProductInfo = {
        ...rest,
        price: isInProductSelection ? valueNewPrice : productInfo.price,
      };

      if (updatedProductInfo.variants) {
        updatedProductInfo.variants = updatedProductInfo.variants.map(
          ({taxedPrice, ...variant}) => ({
            ...variant,
            price: valueNewPrice,
          })
        );
      }

      return cleanObject(updatedProductInfo);
    }

    return productInfo;
  });

  return updatedProductsInfo;
};

export const updateDataProductsNewStock = (
  productsInfo,
  productSelection,
  valueNewStock,
  optionSelectedType: 'ADD' | 'UPDATE'
) => {
  const updatedProductsInfo = productsInfo.map(productInfo => {
    const isInProductSelection = productSelection.some(
      productS => productS.id === productInfo.productId
    );

    if (
      isInProductSelection ||
      productInfo.stock !== undefined ||
      valueNewStock !== null
    ) {
      let updatedStock = productInfo.stock;

      if (optionSelectedType === 'ADD') {
        updatedStock = (updatedStock || 0) + valueNewStock;
      } else if (optionSelectedType === 'UPDATE') {
        updatedStock = valueNewStock;
      }

      const {taxedPrice, price, offerPrice, categories, ...rest} = productInfo;

      const updatedProductInfo = {
        ...rest,
        stock: isInProductSelection ? updatedStock : productInfo.stock,
      };

      if (updatedProductInfo.variants) {
        updatedProductInfo.variants = updatedProductInfo.variants.map(
          ({taxedPrice, offerPrice, ...variant}) => ({
            ...variant,
            offerPrice: String(offerPrice),
            stock:
              optionSelectedType === 'ADD'
                ? (variant.stock || 0) + valueNewStock
                : valueNewStock,
          })
        );
      }

      return updatedProductInfo;
    }

    return productInfo;
  });

  return updatedProductsInfo;
};

export const updateDataProductsNewCategories = (
  productsInfo,
  productSelection,
  valueNewCategories
) => {
  const updatedProductsInfo = productsInfo.map(productInfo => {
    const isInProductSelection = productSelection.some(
      productS => productS.id === productInfo.productId
    );

    if (isInProductSelection || productInfo.categories !== undefined) {
      let updatedCategories = [...(productInfo.categories || [])];

      if (!updatedCategories.includes(valueNewCategories)) {
        updatedCategories.push(valueNewCategories);
      }

      const {
        taxedPrice,
        offerPrice,
        price,
        stock,
        variants,
        ...rest
      } = productInfo;

      const updatedProductInfo = {
        ...rest,
        categories: updatedCategories,
      };

      return updatedProductInfo;
    }

    return productInfo;
  });

  return updatedProductsInfo;
};
